import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getTicketAudit } from "../../services/kanbanEnterpriseService";

const FIELD_LABELS = {
  userId: "Vendedor",
  queueId: "Fila",
  whatsappId: "Conexao",
  priority: "Prioridade",
  status: "Status",
  kanbanColumnId: "Lane Kanban",
  kanbanHiddenFromChat: "Oculto do Chat",
  enteredLaneAt: "Entrada na Lane",
};

const PRIORITY_LABELS = {
  urgent: "Urgente",
  high: "Alta",
  normal: "Normal",
  low: "Baixa",
};

const ACTION_LABELS = {
  CREATE: "Criou",
  UPDATE: "Alterou",
  DELETE: "Removeu",
};

const ACTION_COLORS = {
  CREATE: "#4CAF50",
  UPDATE: "#2196F3",
  DELETE: "#F44336",
};

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    padding: theme.spacing(0),
    minHeight: 200,
    maxHeight: 500,
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
  },
  emptyText: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  timeline: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  entry: {
    display: "flex",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(0,0,0,0.015)",
    },
  },
  entryAvatar: {
    width: 32,
    height: 32,
    fontSize: "0.75rem",
    fontWeight: 600,
    flexShrink: 0,
    marginTop: 2,
  },
  entryBody: {
    flex: 1,
    minWidth: 0,
  },
  entryHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: 2,
  },
  userName: {
    fontWeight: 600,
    fontSize: "0.82rem",
    color: theme.palette.text.primary,
  },
  actionBadge: {
    fontSize: "0.65rem",
    fontWeight: 700,
    borderRadius: 4,
    padding: "1px 6px",
    color: "#fff",
    lineHeight: 1.4,
  },
  entryTime: {
    fontSize: "0.72rem",
    color: theme.palette.text.disabled,
    marginLeft: "auto",
    whiteSpace: "nowrap",
  },
  changesList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  changeItem: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    display: "flex",
    alignItems: "baseline",
    gap: 4,
  },
  fieldName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "0.76rem",
  },
  oldValue: {
    textDecoration: "line-through",
    color: theme.palette.text.disabled,
    fontSize: "0.74rem",
  },
  newValue: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    fontSize: "0.74rem",
  },
  arrow: {
    color: theme.palette.text.disabled,
    fontSize: "0.72rem",
    margin: "0 2px",
  },
  source: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    marginTop: 2,
    fontStyle: "italic",
  },
}));

const formatValue = (field, value) => {
  if (value === null || value === undefined) return "nenhum";
  if (field === "priority") return PRIORITY_LABELS[value] || value;
  if (field === "kanbanHiddenFromChat") return value ? "Sim" : "Nao";
  return String(value);
};

const formatDate = (dateStr) => {
  try {
    const d = typeof dateStr === "string" ? parseISO(dateStr) : dateStr;
    return format(d, "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "";
  }
};

const SOURCE_LABELS = {
  manual: "Acao manual",
  automation: "Automacao",
  system: "Sistema",
  api: "API",
};

const TicketAuditDialog = ({ open, ticketId, onClose }) => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !ticketId) return;
    let cancelled = false;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const data = await getTicketAudit(ticketId);
        if (!cancelled) setLogs(data || []);
      } catch (err) {
        console.error("[TicketAuditDialog] error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLogs();
    return () => { cancelled = true; };
  }, [open, ticketId]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className={classes.dialogTitle}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Historico de Alteracoes — Ticket #{ticketId}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <DialogContent className={classes.content}>
        {loading ? (
          <div className={classes.loadingCenter}>
            <CircularProgress size={28} />
          </div>
        ) : logs.length === 0 ? (
          <Typography className={classes.emptyText}>
            Nenhuma alteracao registrada para este ticket.
          </Typography>
        ) : (
          <ul className={classes.timeline}>
            {logs.map((log) => {
              const userName = log.user?.name || `User #${log.userId}`;
              const userImg = log.user?.profileImage || undefined;
              const initial = (userName || "?").charAt(0).toUpperCase();
              const changes = log.changedFields || [];
              const source = log.metadata?.source;

              return (
                <li key={log.id} className={classes.entry}>
                  <Avatar
                    className={classes.entryAvatar}
                    src={userImg}
                  >
                    {initial}
                  </Avatar>
                  <div className={classes.entryBody}>
                    <div className={classes.entryHeader}>
                      <span className={classes.userName}>{userName}</span>
                      <span
                        className={classes.actionBadge}
                        style={{
                          backgroundColor: ACTION_COLORS[log.action] || "#757575",
                        }}
                      >
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                      <span className={classes.entryTime}>
                        {formatDate(log.createdAt)}
                      </span>
                    </div>

                    {changes.length > 0 && (
                      <ul className={classes.changesList}>
                        {changes.map((ch, idx) => (
                          <li key={idx} className={classes.changeItem}>
                            <span className={classes.fieldName}>
                              {FIELD_LABELS[ch.field] || ch.field}:
                            </span>
                            <span className={classes.oldValue}>
                              {formatValue(ch.field, ch.oldValue)}
                            </span>
                            <span className={classes.arrow}>&rarr;</span>
                            <span className={classes.newValue}>
                              {formatValue(ch.field, ch.newValue)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {source && (
                      <div className={classes.source}>
                        via {SOURCE_LABELS[source] || source}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(TicketAuditDialog);
